<section class="sav-expert-detail-page">
  <div class="sav-expert-container">
    <app-breadcrumb [items]="crumbs()" />

    <!-- Stacked hero: identity (photo + name + role) on one row, then the bio, then the actions,
         so the bio and buttons run the full container width instead of a column beside the photo. -->
    <header class="sav-expert-hero">
      <div class="sav-expert-hero-identity">
        <div class="sav-expert-hero-photo">
          <img
            [src]="author().imageUrl"
            [alt]="author().name || 'Author profile'"
            (error)="showFallbackImage($event)"
          />
        </div>

        <div class="sav-expert-hero-identity-text">
          <span class="sav-expert-eyebrow">SavingArena Expert</span>
          @if (author().name) {
            <h1>{{ author().name }}</h1>
          }
          @if (author().role) {
            <span class="sav-expert-role">{{ author().role }}</span>
          }
        </div>
      </div>

      @if (author().shortDescriptionHtml) {
        <div class="sav-expert-short-description" [innerHTML]="author().shortDescriptionHtml"></div>
      }

      <div class="sav-expert-hero-actions">
        <a [routerLink]="[]" fragment="articles" class="sav-expert-btn sav-expert-btn-primary"
          >View Articles</a
        >
        <a [routerLink]="'/experts'" class="sav-expert-btn sav-expert-btn-light">All Experts</a>
      </div>
    </header>

    <!-- Figures with nothing behind them are dropped in the component, so the band never shows
         an "N/A" cell beside real numbers. -->
    @if (stats().length) {
      <section class="sav-expert-stats" aria-label="Expert stats">
        @for (stat of stats(); track stat.label) {
          <div class="sav-expert-stat">
            <strong>{{ stat.value }}</strong>
            <span>{{ stat.label }}</span>
          </div>
        }
      </section>
    }

    <main class="sav-expert-layout">
      <div class="sav-expert-content">
        @if (author().bioHtml) {
          <section class="sav-expert-block">
            @if (author().name) {
              <app-section-heading
                [heading]="'About ' + author().name"
                subtitle="Background, focus areas, and how this expert approaches savings content."
              />
            }
            <div class="sav-expert-prose" [innerHTML]="author().bioHtml"></div>
          </section>
        }

        @if (author().expertise.length) {
          <section class="sav-expert-block">
            <app-section-heading
              heading="Areas of expertise"
              subtitle="The topics this expert writes about and reviews most often."
            />
            <div class="sav-expert-tags">
              @for (item of author().expertise; track item) {
                <span>{{ item }}</span>
              }
            </div>
          </section>
        }

        <section class="sav-expert-block" id="articles">
          <app-section-heading
            [heading]="'Latest by ' + author().name"
            subtitle="Recent guides and explainers published on SavingArena."
          >
            <a [routerLink]="'/blogs'" class="sav-expert-head-link">All posts →</a>
          </app-section-heading>

          @if (latestPosts().length) {
            <!-- Shared blog card + grid: 3 per row, because the expert sidebar takes the rest. -->
            <div class="sav-blog-grid has-sidebar">
              @for (post of latestPosts(); track post.id) {
                <app-blog-post-card
                  [post]="{
                    slug: post.slug,
                    title: post.title,
                    excerpt: post.excerpt,
                    imageUrl: post.imageUrl,
                    category: post.category,
                    readTime: post.readTime,
                  }"
                />
              }
            </div>
          } @else {
            <div class="sav-expert-empty-posts">
              <h3>Articles are being prepared</h3>
              <p>New expert content will appear here once published.</p>
            </div>
          }
        </section>

        <!-- The stores this expert last worked on. Uses the site-standard store tile so it reads
             the same as the store grids on the category and home pages. -->
        @if (updatedStores().length) {
          <section class="sav-expert-block" id="stores">
            <app-section-heading
              heading="Stores recently updated"
              subtitle="The newest store pages this expert has reviewed and refreshed."
            >
              <a [routerLink]="'/storelist'" class="sav-expert-head-link">All stores →</a>
            </app-section-heading>

            <div class="sav-trending-stores-grid sav-expert-store-grid">
              @for (store of updatedStores(); track store.id) {
                <a class="sav-trending-store-card" [routerLink]="['/', store.slug]">
                  <span class="sav-store-tile-logo-box">
                    @if (store.logoUrl) {
                      <img
                        [src]="store.logoUrl"
                        [alt]="store.name + ' logo'"
                        loading="lazy"
                        class="sav-store-tile-store-logo"
                      />
                    } @else {
                      <svg
                        class="sav-store-tile-store-placeholder"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          d="M4 10.2 5.4 4h13.2l1.4 6.2v.3a3 3 0 0 1-5.2 2 3 3 0 0 1-4.6 0 3 3 0 0 1-5.2-2v-.3Zm1 3.7a4.5 4.5 0 0 0 4.2-.6 4.5 4.5 0 0 0 5.6 0 4.5 4.5 0 0 0 4.2.6V20H5v-6.1Zm2 1.6V18h10v-2.5H7Z"
                          fill="currentColor"
                        ></path>
                      </svg>
                    }
                  </span>
                  <span class="sav-store-tile-divider" aria-hidden="true"></span>
                  <span class="sav-store-tile-store-meta">
                    <span class="sav-store-tile-brand-name">{{ store.name }}</span>
                    @if (store.updatedLabel) {
                      <span class="sav-store-tile-count">{{ store.updatedLabel }}</span>
                    }
                  </span>
                </a>
              }
            </div>
          </section>
        }
      </div>

      <aside class="sav-expert-sidebar" aria-label="Expert sidebar">
        <section class="sav-expert-side-card">
          <h3>Expert details</h3>
          <dl class="sav-expert-side-list">
            <div>
              <dt>Role</dt>
              <dd>{{ author().role || '—' }}</dd>
            </div>
            <div>
              <dt>Focus</dt>
              <dd>{{ author().focus || '—' }}</dd>
            </div>
            <div>
              <dt>Articles</dt>
              <dd>{{ author().articleCount }}</dd>
            </div>
            <div>
              <dt>Joined</dt>
              <dd>{{ author().joined || '—' }}</dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>{{ author().location || '—' }}</dd>
            </div>
          </dl>
        </section>

        @if (author().popularTopics.length) {
          <section class="sav-expert-side-card">
            <h3>Popular topics</h3>
            <ul class="sav-expert-topic-list">
              @for (topic of author().popularTopics; track topic.label) {
                <li>
                  <a [routerLink]="topic.routeLink">{{ topic.label }}</a>
                </li>
              }
            </ul>
          </section>
        }
      </aside>
    </main>

    <section class="sav-expert-cta">
      <div>
        <span class="sav-expert-eyebrow">SavingArena Experts</span>
        <h2>
          Explore more verified coupon, store and shopping guidance<app-dev-flag
            note="Static CTA — not admin-managed"
          />
        </h2>
        <p>Meet the full team creating practical savings content across SavingArena.</p>
      </div>
      <a [routerLink]="'/experts'" class="sav-expert-btn sav-expert-btn-primary"
        >View all experts</a
      >
    </section>
  </div>
</section>
